import { useState, useEffect } from 'react';
import axios from 'axios';
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isToday } from 'date-fns';
import { Calendar as CalendarIcon, List, Check, X, ChevronLeft, ChevronRight, Clock, User, Phone, Mail, DollarSign, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AppointmentsPage() {
  const [appointments, setAppointments] = useState([]);
  const [view, setView] = useState('list');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchAppointments();
  }, []);

  const fetchAppointments = async () => {
    try {
      const { data } = await axios.get(`${API}/appointments`, { withCredentials: true });
      setAppointments(data);
    } catch (error) {
      toast.error('Failed to fetch appointments');
    } finally {
      setLoading(false);
    }
  };

  const confirmPayment = async (id) => {
    try {
      await axios.put(`${API}/appointments/${id}/confirm-payment`, {}, { withCredentials: true });
      toast.success('Payment confirmed! Customer notified.');
      fetchAppointments();
      setSelectedAppointment(null);
    } catch (error) {
      toast.error('Failed to confirm payment');
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/appointments/${id}/status?status=${status}`, {}, { withCredentials: true });
      toast.success(`Status updated to ${status}`);
      fetchAppointments();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const deleteAppointment = async (id) => {
    if (!window.confirm('Are you sure you want to delete this appointment?')) return;
    try {
      await axios.delete(`${API}/appointments/${id}`, { withCredentials: true });
      toast.success('Appointment deleted');
      fetchAppointments();
      setSelectedAppointment(null);
    } catch (error) {
      toast.error('Failed to delete appointment');
    }
  };

  const filteredAppointments = appointments.filter(a => {
    if (filter === 'all') return true;
    if (filter === 'pending') return a.status === 'pending';
    if (filter === 'confirmed') return a.status === 'confirmed';
    if (filter === 'completed') return a.status === 'completed';
    return true;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'confirmed': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const daysInMonth = eachDayOfInterval({
    start: startOfMonth(currentMonth),
    end: endOfMonth(currentMonth)
  });

  const getAppointmentsForDay = (day) => {
    return appointments.filter(a => isSameDay(parseISO(a.date), day));
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#9C6A48]"></div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8" data-testid="appointments-page">
      <div className="mb-8">
        <h1 className="text-3xl font-light text-[#2B2623]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          Appointments
        </h1>
        <p className="text-[#756A63] mt-1">Manage your bookings and confirm payments</p>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap gap-4 mb-6">
        <div className="flex gap-2">
          <Button
            variant={view === 'list' ? 'default' : 'outline'}
            onClick={() => setView('list')}
            className={view === 'list' ? 'bg-[#9C6A48] hover:bg-[#8B5A33]' : 'border-[#E3DACD]'}
            data-testid="view-list-btn"
          >
            <List className="w-4 h-4 mr-2" />
            List
          </Button>
          <Button
            variant={view === 'calendar' ? 'default' : 'outline'}
            onClick={() => setView('calendar')}
            className={view === 'calendar' ? 'bg-[#9C6A48] hover:bg-[#8B5A33]' : 'border-[#E3DACD]'}
            data-testid="view-calendar-btn"
          >
            <CalendarIcon className="w-4 h-4 mr-2" />
            Calendar
          </Button>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-[150px] border-[#E3DACD]" data-testid="filter-select">
            <SelectValue placeholder="Filter" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="confirmed">Confirmed</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* List View */}
      {view === 'list' && (
        <div className="bg-white rounded-xl border border-[#E3DACD] overflow-hidden">
          {filteredAppointments.length === 0 ? (
            <div className="p-8 text-center text-[#756A63]">
              No appointments found
            </div>
          ) : (
            <div className="divide-y divide-[#E3DACD]">
              {filteredAppointments.map((apt) => (
                <div
                  key={apt.id}
                  className="p-4 hover:bg-[#FAF7F2] transition-colors cursor-pointer"
                  onClick={() => setSelectedAppointment(apt)}
                  data-testid={`appointment-item-${apt.id}`}
                >
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-medium text-[#2B2623]">{apt.customer_name}</h3>
                        <Badge className={getStatusColor(apt.status)}>{apt.status}</Badge>
                        {!apt.payment_confirmed && apt.status !== 'cancelled' && (
                          <Badge className="bg-orange-100 text-orange-800">Awaiting Payment</Badge>
                        )}
                      </div>
                      <p className="text-sm text-[#756A63]">{apt.service_name}</p>
                      <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-[#756A63]">
                        <span className="flex items-center gap-1">
                          <CalendarIcon className="w-4 h-4" />
                          {format(parseISO(apt.date), 'MMM d, yyyy')}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {apt.time}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {!apt.payment_confirmed && apt.status === 'pending' && (
                        <Button
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); confirmPayment(apt.id); }}
                          className="bg-green-600 hover:bg-green-700 text-white"
                          data-testid={`confirm-payment-btn-${apt.id}`}
                        >
                          <Check className="w-4 h-4 mr-1" />
                          Confirm Payment
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Calendar View */}
      {view === 'calendar' && (
        <div className="bg-white rounded-xl border border-[#E3DACD] p-4">
          <div className="flex items-center justify-between mb-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentMonth(new Date(currentMonth.setMonth(currentMonth.getMonth() - 1)))}
              className="border-[#E3DACD]"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <h2 className="text-lg font-medium text-[#2B2623]">
              {format(currentMonth, 'MMMM yyyy')}
            </h2>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentMonth(new Date(currentMonth.setMonth(currentMonth.getMonth() + 1)))}
              className="border-[#E3DACD]"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>

          <div className="grid grid-cols-7 gap-1 mb-2">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="text-center text-sm text-[#756A63] font-medium py-2">
                {day}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {Array(startOfMonth(currentMonth).getDay()).fill(null).map((_, i) => (
              <div key={`empty-${i}`} className="h-24"></div>
            ))}
            {daysInMonth.map(day => {
              const dayAppointments = getAppointmentsForDay(day);
              return (
                <div
                  key={day.toISOString()}
                  className={`h-24 p-1 border border-[#E3DACD] rounded-lg overflow-hidden ${
                    isToday(day) ? 'bg-[#9C6A48]/5 border-[#9C6A48]' : ''
                  }`}
                >
                  <div className={`text-sm mb-1 ${isToday(day) ? 'text-[#9C6A48] font-medium' : 'text-[#756A63]'}`}>
                    {format(day, 'd')}
                  </div>
                  <div className="space-y-1">
                    {dayAppointments.slice(0, 2).map(apt => (
                      <div
                        key={apt.id}
                        onClick={() => setSelectedAppointment(apt)}
                        className={`text-xs p-1 rounded truncate cursor-pointer ${getStatusColor(apt.status)}`}
                      >
                        {apt.time} - {apt.customer_name}
                      </div>
                    ))}
                    {dayAppointments.length > 2 && (
                      <div className="text-xs text-[#756A63]">+{dayAppointments.length - 2} more</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Appointment Detail Dialog */}
      <Dialog open={!!selectedAppointment} onOpenChange={() => setSelectedAppointment(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Appointment Details</DialogTitle>
          </DialogHeader>
          {selectedAppointment && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Badge className={getStatusColor(selectedAppointment.status)}>
                  {selectedAppointment.status}
                </Badge>
                {!selectedAppointment.payment_confirmed && (
                  <Badge className="bg-orange-100 text-orange-800">Payment Pending</Badge>
                )}
                {selectedAppointment.payment_confirmed && (
                  <Badge className="bg-green-100 text-green-800">Payment Confirmed</Badge>
                )}
              </div>

              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-3">
                  <User className="w-4 h-4 text-[#756A63]" />
                  <span className="text-[#2B2623]">{selectedAppointment.customer_name}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="w-4 h-4 text-[#756A63]" />
                  <span className="text-[#2B2623]">{selectedAppointment.customer_email}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="w-4 h-4 text-[#756A63]" />
                  <span className="text-[#2B2623]">{selectedAppointment.customer_phone}</span>
                </div>
                <div className="flex items-center gap-3">
                  <CalendarIcon className="w-4 h-4 text-[#756A63]" />
                  <span className="text-[#2B2623]">
                    {format(parseISO(selectedAppointment.date), 'MMMM d, yyyy')} at {selectedAppointment.time}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <DollarSign className="w-4 h-4 text-[#756A63]" />
                  <span className="text-[#2B2623]">{selectedAppointment.service_name}</span>
                </div>
                {selectedAppointment.notes && (
                  <div className="bg-[#FAF7F2] p-3 rounded-lg">
                    <p className="text-xs text-[#756A63] mb-1">Notes:</p>
                    <p className="text-[#2B2623]">{selectedAppointment.notes}</p>
                  </div>
                )}
              </div>

              <div className="border-t border-[#E3DACD] pt-4">
                <p className="text-sm text-[#756A63] mb-2">Update Status:</p>
                <div className="flex flex-wrap gap-2">
                  {['pending', 'confirmed', 'completed', 'cancelled'].map(status => (
                    <Button
                      key={status}
                      size="sm"
                      variant={selectedAppointment.status === status ? 'default' : 'outline'}
                      onClick={() => updateStatus(selectedAppointment.id, status)}
                      className={selectedAppointment.status === status ? 'bg-[#9C6A48]' : 'border-[#E3DACD]'}
                    >
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="flex gap-2">
            {selectedAppointment && !selectedAppointment.payment_confirmed && selectedAppointment.status === 'pending' && (
              <Button
                onClick={() => confirmPayment(selectedAppointment.id)}
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid="confirm-payment-modal-btn"
              >
                <Check className="w-4 h-4 mr-2" />
                Confirm Payment
              </Button>
            )}
            <Button
              variant="destructive"
              onClick={() => deleteAppointment(selectedAppointment?.id)}
              data-testid="delete-appointment-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
